import React from 'react';
import { motion } from 'framer-motion';
import { gameUIVariants } from '../../lib/animations';

interface CosmicCardProps {
  children: React.ReactNode;
  variant?: 'default' | 'holographic' | 'data' | 'status' | 'interactive';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  onClick?: () => void;
  interactive?: boolean;
  status?: 'online' | 'offline' | 'warning' | 'error';
  title?: string;
  subtitle?: string;
  icon?: React.ReactNode;
  glow?: boolean;
}

const CosmicCard: React.FC<CosmicCardProps> = ({
  children,
  variant = 'default',
  size = 'md',
  className = '',
  onClick,
  interactive = false,
  status,
  title,
  subtitle,
  icon,
  glow = false
}) => {
  const getVariantStyles = () => {
    switch (variant) {
      case 'holographic':
        return 'cosmic-glass animate-hologram';
      case 'data':
        return 'bg-cosmic-dark/90 border-stellar-cyan/30';
      case 'status':
        return 'bg-cosmic-dark/80 border-stellar-green/30';
      case 'interactive':
        return 'cosmic-glass cursor-pointer hover:border-stellar-blue/60';
      default:
        return 'cosmic-glass';
    }
  };

  const getSizeStyles = () => {
    switch (size) {
      case 'sm':
        return 'p-4';
      case 'md':
        return 'p-6';
      case 'lg':
        return 'p-8';
      default:
        return 'p-6';
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'online':
        return 'border-stellar-green text-stellar-green';
      case 'offline':
        return 'border-stellar-cyan text-stellar-cyan';
      case 'warning':
        return 'border-warning-amber text-warning-amber';
      case 'error':
        return 'border-danger-red text-danger-red';
      default:
        return 'border-stellar-blue text-stellar-blue';
    }
  };

  const getStatusGlow = () => {
    switch (status) {
      case 'online':
        return '0 0 10px var(--stellar-green)';
      case 'offline':
        return '0 0 10px var(--stellar-cyan)';
      case 'warning':
        return '0 0 10px var(--warning-amber)';
      case 'error':
        return '0 0 10px var(--danger-red)';
      default:
        return '0 0 10px var(--stellar-blue)';
    }
  };

  return (
    <motion.div
      className={`
        relative rounded-lg border transition-all duration-300
        ${getVariantStyles()}
        ${getSizeStyles()}
        ${interactive ? 'cursor-pointer' : ''}
        ${glow ? 'animate-pulse-glow' : ''}
        ${className}
      `}
      variants={gameUIVariants}
      whileHover={interactive ? "cardHover" : undefined}
      whileTap={interactive ? "cardTap" : undefined}
      onClick={interactive ? onClick : undefined}
      style={{
        boxShadow: glow ? getStatusGlow() : undefined
      }}
    >
      {/* Top border accent */}
      <div className="absolute top-0 left-0 w-full h-1 bg-stellar-gradient" />

      {/* Status indicator */}
      {status && (
        <div className="absolute top-2 right-2 flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full bg-current ${getStatusColor()}`} />
          <span className="text-xs font-mono uppercase tracking-wider">
            {status}
          </span>
        </div>
      )}

      {/* Header */}
      {(title || icon) && (
        <div className="flex items-center gap-3 mb-4">
          {icon && (
            <motion.div
              className="text-2xl text-stellar-blue"
              animate={{ rotate: [0, 360] }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            >
              {icon}
            </motion.div>
          )}
          <div>
            {title && (
              <h3 className="text-lg font-display font-semibold text-white mb-1">
                {title}
              </h3>
            )}
            {subtitle && (
              <p className="text-sm text-stellar-cyan font-mono">
                {subtitle}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="relative z-10">
        {children}
      </div>

      {/* Holographic overlay */}
      {variant === 'holographic' && (
        <motion.div
          className="absolute inset-0 bg-nebula-radial opacity-0 group-hover:opacity-100"
          transition={{ duration: 0.3 }}
        />
      )}

      {/* Interactive glow effect */}
      {interactive && (
        <motion.div
          className="absolute inset-0 rounded-lg border-stellar-blue/20"
          initial={{ opacity: 0 }}
          whileHover={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
          style={{
            boxShadow: '0 0 20px rgba(0, 212, 255, 0.3)'
          }}
        />
      )}
    </motion.div>
  );
};

// Specialized card variants
export const HolographicCard: React.FC<Omit<CosmicCardProps, 'variant'>> = (props) => (
  <CosmicCard variant="holographic" {...props} />
);

export const DataCard: React.FC<Omit<CosmicCardProps, 'variant'>> = (props) => (
  <CosmicCard variant="data" {...props} />
);

export const StatusCard: React.FC<Omit<CosmicCardProps, 'variant'>> = (props) => (
  <CosmicCard variant="status" {...props} />
);

export const InteractiveCard: React.FC<Omit<CosmicCardProps, 'variant'>> = (props) => (
  <CosmicCard variant="interactive" interactive {...props} />
);

// Metric Card Component
interface MetricCardProps {
  title: string;
  value: string | number;
  change?: string;
  trend?: 'up' | 'down' | 'neutral';
  icon?: React.ReactNode;
  className?: string;
}

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  change,
  trend = 'neutral',
  icon,
  className = ''
}) => {
  const getTrendColor = () => {
    switch (trend) {
      case 'up':
        return 'text-success-emerald';
      case 'down':
        return 'text-danger-red';
      default:
        return 'text-stellar-cyan';
    }
  };

  const getTrendIcon = () => {
    switch (trend) {
      case 'up':
        return '↗';
      case 'down':
        return '↘';
      default:
        return '→';
    }
  };

  return (
    <CosmicCard
      variant="data"
      size="sm"
      className={className}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-stellar-cyan font-mono uppercase tracking-wider">
            {title}
          </p>
          <p className="text-2xl font-display font-semibold text-white mt-1">
            {value}
          </p>
          {change && (
            <p className={`text-xs font-mono mt-1 ${getTrendColor()}`}>
              {getTrendIcon()} {change}
            </p>
          )}
        </div>
        {icon && (
          <div className="text-2xl text-stellar-blue">
            {icon}
          </div>
        )}
      </div>
    </CosmicCard>
  );
};

// Status Indicator Component
interface StatusIndicatorProps {
  status: 'online' | 'offline' | 'warning' | 'error';
  label?: string;
  size?: 'sm' | 'md' | 'lg';
}

export const StatusIndicator: React.FC<StatusIndicatorProps> = ({
  status,
  label,
  size = 'md'
}) => {
  const getStatusStyles = () => {
    const baseStyles = 'rounded-full animate-pulse';
    const sizeStyles = {
      sm: 'w-2 h-2',
      md: 'w-3 h-3',
      lg: 'w-4 h-4'
    };

    switch (status) {
      case 'online':
        return `${baseStyles} ${sizeStyles[size]} bg-stellar-green`;
      case 'offline':
        return `${baseStyles} ${sizeStyles[size]} bg-stellar-cyan`;
      case 'warning':
        return `${baseStyles} ${sizeStyles[size]} bg-warning-amber`;
      case 'error':
        return `${baseStyles} ${sizeStyles[size]} bg-danger-red`;
      default:
        return `${baseStyles} ${sizeStyles[size]} bg-stellar-blue`;
    }
  };

  return (
    <div className="flex items-center gap-2">
      <div className={getStatusStyles()} />
      {label && (
        <span className="text-xs font-mono uppercase tracking-wider text-stellar-cyan">
          {label}
        </span>
      )}
    </div>
  );
};

export default CosmicCard; 